import { useAVToggle, useHMSActions, useHMSStore, selectRoom } from "@100mslive/react-sdk";

import ButtonComponent from "@/components/button/Button";
import IconVideoOn from "@/components/icons/IconVideoOn";
import IconMicOn from "@/components/icons/IconMicOn";
import IconMicOff from "@/components/icons/IconMicOff";
import IconVideoOff from "@/components/icons/IconVideoOff";
import IconCallEnd from "@/components/icons/IconCallEnd";
import IconGrace from "@/components/icons/IconGrace";
import "@/styles/components/sessionFooter.scss";
import IconReport from "@/components/icons/IconReport";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrentForm, setFormProps, setModalOpen } from "@/reducers/commonSlice";
import { updateRoomStatusById } from "@/services/users/updateRoomStatus";
import { showNotification } from "@/components/toaster/Toaster";
import { extendSessionDuration } from "@/services/services/getAvailableGraceTime";

import socket from "@/socket";
import { crateReport } from "@/services/users/getReportCategory";

const Footer = ({ isHost, bookingId, setCallEnded }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hmsActions = useHMSActions();
  const room = useHMSStore(selectRoom);
  const { id: sessionId } = useParams();
  const isGuest = window.location.pathname?.includes("guest");

  const { isLocalAudioEnabled, isLocalVideoEnabled, toggleAudio, toggleVideo } = useAVToggle();

  const requestPermission = async (type) => {
    try {
      // Check if media devices are supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showNotification("error", "Media devices are not supported in your browser.");
        return;
      }

      const permissionName = type === "video" ? "camera" : "microphone";

      // Check permission status if available
      if (navigator.permissions && navigator.permissions.query) {
        const permission = await navigator.permissions.query({
          name: permissionName,
        });
        if (permission.state === "denied") {
          showNotification(
            "error",
            `Access to ${permissionName} is denied. Please enable it in your browser settings.`
          );
          return;
        }
      }

      // Request media access
      const constraints = type === "video" ? { video: true } : { audio: true };
      await navigator.mediaDevices.getUserMedia(constraints);

      // Toggle media after permission is granted
      type === "video" ? toggleVideo() : toggleAudio();
    } catch (error) {
      console.error(`${type} permission request failed:`, error);
      showNotification(
        "error",
        `Failed to access ${type}. Please enable permissions in your browser settings.`
      );
    }
  };

  const handleToggleVideo = () => requestPermission("video");
  const handleToggleAudio = () => requestPermission("audio");

  const leaveRoom = () => {
    console.log(isHost, "isHost");
    showEndCallConfirmation();

    // setReviewStep("session");
    // setIsReviewModalOpen(true);
  };

  const addAndRequestGraceTime = () => {
    dispatch(setCurrentForm("requestGraceTime"));
    dispatch(setModalOpen(true));
    dispatch(
      setFormProps({
        message: isHost ? "Add Grace Time" : "Grace Time",
        details: { isHost },
        onSubmit: async (selectedTime) => {
          if (room && !isHost) {
            const roomId = room?.id;
            const token = localStorage.getItem("token");
            console.log("Student requesting grace time:", { roomId, selectedTime });
            socket.emit("sendGraceTimeRequest", {
              roomId,
              time: selectedTime,
              token,
            });
            dispatch(setModalOpen(false));
            showNotification(
              "success",
              "Grace Time Requested",
              "Your request for grace time has been sent to the host."
            );
          } else if (room && isHost) {
            const roomId = room?.id;
            console.log("Host adding grace time directly:", { roomId, selectedTime });
            try {
              const response = await extendSessionDuration(roomId, selectedTime);
              console.log("Grace time extension response:", response);
              // Emit to both mentor and student
              socket.emit("updateDuration", {
                roomId,
                time: selectedTime,
                isApproved: true,
                message: response.msg,
                newDuration: response.maxDuration,
                isHost: true, // Add flag to identify host-initiated update
              });
              // Also emit a separate event for immediate time update
              socket.emit("forceTimeUpdate", {
                roomId,
                newDuration: response.maxDuration,
              });
              showNotification("success", "Grace Time Added", response.msg);
            } catch (error) {
              console.error("Error extending session duration:", error);
              showNotification("error", "Error", "Failed to add grace time. Please try again.");
            }
            dispatch(setModalOpen(false));
          }
        },
      })
    );
  };

  const showEndCallConfirmation = () => {
    dispatch(setCurrentForm("confirmDelete"));
    dispatch(setModalOpen(true));
    dispatch(
      setFormProps({
        onSubmit: () => {
          if (isHost) {
            updateRoomStatusById(bookingId, {
              enable: false,
            })
              .then((res) => {
                console.log(res);
                hmsActions.leave();
                hmsActions.endRoom();
              })
              .catch((err) => console.log(err));
          }
          hmsActions.leave();
          navigate(`/callend/?sid=${sessionId}`);
          dispatch(setModalOpen(false));
        },
        message: "Are you sure you want to end this call?",
      })
    );
  };

  const handleReport = () => {
    dispatch(setCurrentForm("report"));
    dispatch(
      setFormProps({
        categoryType: "Session",
        handleSubmit: (data) => {
          if (data.categoryId && data.description) {
            data.metadata = { sessionId };
            crateReport(data).then((res) => {
              console.log(res);
              dispatch(setModalOpen(false));
              dispatch(setCurrentForm("successModal"));
              dispatch(setModalOpen(true));
              dispatch(
                setFormProps({
                  message: "Your report has been submitted successfully.",
                  btnLabel: "Close",
                  onSubmit: () => {
                    dispatch(setModalOpen(false));
                  },
                })
              );
              if (isHost) {
                updateRoomStatusById(bookingId, {
                  enable: false,
                })
                  .then((res) => {
                    hmsActions.leave();
                    hmsActions.endRoom();
                    navigate(`/callend/?sid=${sessionId}`);
                  })
                  .catch((err) => console.log(err));
              } else {
                hmsActions.leave();
                navigate(`/callend/?sid=${sessionId}`);
              }
            });
          }
        },
      })
    );
    dispatch(setModalOpen(true));
  };

  return (
    <div className="control-bar">
      <ButtonComponent
        label=""
        shape="circle"
        className="end-call"
        icon={<IconCallEnd />}
        onClick={leaveRoom}
      />
      <ButtonComponent
        label=""
        shape="circle"
        icon={isLocalVideoEnabled ? <IconVideoOn /> : <IconVideoOff />}
        onClick={handleToggleVideo}
      />
      <ButtonComponent
        label=""
        shape="circle"
        icon={isLocalAudioEnabled ? <IconMicOn /> : <IconMicOff />}
        onClick={handleToggleAudio}
      />
      {isGuest ? null : (
        <>
          <ButtonComponent
            label=""
            shape="circle"
            icon={<IconGrace />}
            onClick={addAndRequestGraceTime}
          />
          <ButtonComponent label="" shape="circle" icon={<IconReport />} onClick={handleReport} />
        </>
      )}
    </div>
  );
};

export default Footer;
